import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const TenantContext = createContext();

const API_BASE = process.env.REACT_APP_API_URL || '';
const STORAGE_KEY = 'activeTenantId';
const PERSONAL_KEY = 'personalMode';

export function TenantProvider({ children }) {
  const { token, user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [activeTenant, setActiveTenant] = useState(null);
  const [loading, setLoading] = useState(false);
  // showSwitcher = "User möchte gerade die Auswahlseite sehen, obwohl er einen
  // aktiven Mandanten hat". So bleibt activeTenant erhalten und Abbrechen
  // kann einfach den Switcher-Modus schließen, ohne den Kontext zu verlieren.
  const [showSwitcher, setShowSwitcher] = useState(false);
  // personalMode = User arbeitet ohne Mandant. Chat-Routen sind eh user-skopiert,
  // also funktionieren sie ohne weitere Backend-Änderung. Mandanten-Features
  // (Workflows, Datenquellen, Aufgaben, Notifications) werden ausgeblendet.
  // Persistiert in localStorage damit ein F5 nicht aus dem Modus rauswirft.
  const [personalMode, setPersonalMode] = useState(
    () => localStorage.getItem(PERSONAL_KEY) === 'true'
  );

  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  }), [token]);

  const loadTenants = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/tenants`, {
        headers: authHeaders()
      });
      if (!res.ok) throw new Error('Failed to load tenants');
      const data = await res.json();
      setTenants(data.tenants || []);

      // Im Privat-Modus: aktive Tenant-Auswahl ignorieren — wir bleiben privat,
      // bis der User explizit aussteigt.
      if (localStorage.getItem(PERSONAL_KEY) === 'true') {
        setActiveTenant(null);
        return;
      }

      const stored = localStorage.getItem(STORAGE_KEY);
      const found = (data.tenants || []).find(t => t._id === stored);
      if (found) {
        setActiveTenant(found);
      } else if ((data.tenants || []).length === 1) {
        setActiveTenant(data.tenants[0]);
        localStorage.setItem(STORAGE_KEY, data.tenants[0]._id);
      } else {
        setActiveTenant(null);
        // Stale localStorage räumen (z. B. wenn der gespeicherte Mandant
        // inzwischen deaktiviert oder gelöscht wurde).
        if (stored) localStorage.removeItem(STORAGE_KEY);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, authHeaders]);

  useEffect(() => {
    // BEWUSST nur an `token` gekoppelt — nicht an `user`. refreshUser()
    // ändert die User-Referenz und würde sonst eine Tenant-Reload triggern,
    // während der App.js den Loading-Screen zeigt → ChatLayout unmountet,
    // showTasks/showWorkflows-State geht verloren, User landet wieder im Chat.
    // Tenants laden wir nur, wenn jemand sich ein-/ausloggt (Token wechselt).
    if (token) {
      loadTenants();
    } else {
      setTenants([]);
      setActiveTenant(null);
    }
  }, [token, loadTenants]);

  const switchTenant = useCallback((tenantId) => {
    const t = tenants.find(x => x._id === tenantId);
    if (t) {
      setActiveTenant(t);
      localStorage.setItem(STORAGE_KEY, t._id);
      // Mandantenwechsel beendet automatisch den Privat-Modus
      localStorage.removeItem(PERSONAL_KEY);
      setPersonalMode(false);
      setShowSwitcher(false); // Switcher schließen nach Auswahl
    }
  }, [tenants]);

  const clearActiveTenant = useCallback(() => {
    setActiveTenant(null);
    localStorage.removeItem(STORAGE_KEY);
    setShowSwitcher(false);
  }, []);

  const openSwitcher = useCallback(() => setShowSwitcher(true), []);
  const closeSwitcher = useCallback(() => setShowSwitcher(false), []);

  // In den Privat-Modus wechseln: kein activeTenant mehr, aber auch nicht
  // showSwitcher → App rendert ChatLayout im abgespeckten Modus.
  const enterPersonalMode = useCallback(() => {
    setActiveTenant(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.setItem(PERSONAL_KEY, 'true');
    setPersonalMode(true);
    setShowSwitcher(false);
  }, []);

  // Privat-Modus verlassen → zurück zum Mandanten-Selector
  const exitPersonalMode = useCallback(() => {
    localStorage.removeItem(PERSONAL_KEY);
    setPersonalMode(false);
    setActiveTenant(null);
    setShowSwitcher(false);
  }, []);

  const createTenant = useCallback(async (name) => {
    const res = await fetch(`${API_BASE}/api/tenants`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ name })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to create tenant');
    await loadTenants();
    setActiveTenant(data.tenant);
    localStorage.setItem(STORAGE_KEY, data.tenant._id);
    return data.tenant;
  }, [authHeaders, loadTenants]);

  const tenantFetch = useCallback(async (path, options = {}) => {
    const headers = {
      ...authHeaders(),
      ...(options.headers || {})
    };
    if (activeTenant) {
      headers['x-tenant-id'] = activeTenant._id;
    }
    return fetch(`${API_BASE}${path}`, { ...options, headers });
  }, [authHeaders, activeTenant]);

  return (
    <TenantContext.Provider value={{
      tenants,
      activeTenant,
      loading,
      showSwitcher,
      personalMode,
      loadTenants,
      switchTenant,
      clearActiveTenant,
      openSwitcher,
      closeSwitcher,
      enterPersonalMode,
      exitPersonalMode,
      createTenant,
      tenantFetch
    }}>
      {children}
    </TenantContext.Provider>
  );
}

export const useTenant = () => useContext(TenantContext);
